//import components
import withReadModeField from 'components/shared/CRUD/components/InnerPage/components/withReadModeField';
import MultiStepForm from 'components/shared/MultiStepForm/MultiStepForm';
import {
  ControlledSelect,
  ControlledToggleButtonGroup
} from 'components/shared/ControlledFields';
import { useFormContext } from 'react-hook-form';
import {
  GridCompact6,
  GridContainer
} from 'components/shared/Grid/Grid.components';

// import consts
import {
  endOfEmploymentSettlementFields,
  fieldCategories
} from '../CLASettings.consts';

// import enums
import { EndOfEmploymentSettlementEnums } from '../CLASettings.enums';
import { EndOfEmploymentSettlementFieldLabels } from './EndOfEmploymentSettlementTab.enums';

// import hooks
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetDutyHourTypesTableData } from 'components/admin-portal/Tables/Settings/DutyHourTypes/hooks/useGetDutyHourTypesTableData';

// import styles
import {
  StyledLabelTypography,
  StyledToggleGroupBox
} from './EndOfEmploymentSettlementTab.styles';

// import types
import { TabProps } from '../CLASettings.types';
import { DccFlag } from 'components/types';

//import utils
import { endOfEmploymentSettlementSchema } from '../CLASettings.schema';
import { useEditPageContext } from 'components/shared/CRUD/components/InnerPage/EditPageContext';
import {
  StyledBoxFlexCenterWrap,
  StyledFlexGrowBox
} from 'components/shared/Box/Box.styles';

const ControlledSelectWithReadMode = withReadModeField(ControlledSelect);

const EndOfEmploymentSettlementTab = ({ setCurrentSchema }: TabProps) => {
  const { t } = useTranslation('common', {
    keyPrefix: 'adminPortal.cla.endOfEmploymentSettlement.settings'
  });
  const { isEditable } = useEditPageContext();

  const { data: dhtOptions } = useGetDutyHourTypesTableData();

  const filteredDhtOptions =
    dhtOptions &&
    [...dhtOptions]
      .sort((a, b) => (a.description ?? '').localeCompare(b.description ?? ''))
      .filter(({ dcc }) => dcc?.flags?.includes(DccFlag.ALLOWED_WITH_NOMINAL))
      .map((item) => ({
        label: item.description ?? '',
        value: item.dutyTypeId
      }));

  const { control, watch } = useFormContext();
  const customBalanceBudgetName = watch('balanceBudgetName');

  useEffect(() => {
    setCurrentSchema(endOfEmploymentSettlementSchema);
  }, []);

  return (
    <>
      <StyledToggleGroupBox>
        <ControlledToggleButtonGroup
          control={control}
          name="endOfEmploymentSettlementType"
          color="secondary"
          aria-label="alignment"
          label={t('processingType')}
          disabled={!isEditable}
          inputToggleButtons={[
            {
              label: t('payoutTitle'),
              value: EndOfEmploymentSettlementEnums.PAY_OUT
            },
            {
              label: t('settleTitle'),
              value: EndOfEmploymentSettlementEnums.SETTLE
            },
            {
              label: t('transferTitle'),
              value: EndOfEmploymentSettlementEnums.TRANSFER
            }
          ]}
        />
      </StyledToggleGroupBox>
      {fieldCategories.map(({ key, title }) => (
        <MultiStepForm.Section key={key} title={t(title)}>
          <GridContainer columnSpacing={16} rowSpacing={4}>
            {endOfEmploymentSettlementFields[
              key as keyof typeof endOfEmploymentSettlementFields
            ].map((item, id) => {
              const isBalanceHours =
                item?.label ===
                EndOfEmploymentSettlementFieldLabels.BALANCE_HOURS;

              return (
                <GridCompact6 key={`${key}_grid_${id}`}>
                  <StyledBoxFlexCenterWrap>
                    <StyledLabelTypography variant="body1">
                      {isBalanceHours && customBalanceBudgetName
                        ? customBalanceBudgetName
                        : t(item.label)}
                    </StyledLabelTypography>
                    <StyledFlexGrowBox>
                      <ControlledSelectWithReadMode
                        control={control}
                        variant="outlined"
                        name={item.name}
                        placeholder={t('selectDutyHourType')}
                        label=""
                        options={filteredDhtOptions ?? []}
                        hasClearButton
                        displayEmpty
                        valueGetter="value"
                        labelGetter="label"
                        fullWidth
                      />
                    </StyledFlexGrowBox>
                  </StyledBoxFlexCenterWrap>
                </GridCompact6>
              );
            })}
          </GridContainer>
        </MultiStepForm.Section>
      ))}
    </>
  );
};

export default EndOfEmploymentSettlementTab;
